import type {
  FileRecordProperty,
  UUID
} from '@indigohive/cogfy-types'
import { useState } from 'react'
import { useCogfy } from '../../../../hooks'
import { MicIcon, TrashIcon } from 'lucide-react'
import { Button, ConfirmDeleteDialog } from '../../../../components'
import { useUpload } from '../../../FieldCell/components/FileCell/hooks/useUpload'
import { FileRecordPropertyInputProps } from './FileRecordPropertyInput'
import { useMutation } from '@tanstack/react-query'
import { t } from 'i18next'
import { AudioDuration } from '../../../../components/AudioDuration'

export function FileRecordPropertyInputMicVariant (props: FileRecordPropertyInputProps) {
  const { collectionId, field, record, canEdit, disabled } = props

  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [fileToDeleteId, setFileToDeleteId] = useState<UUID | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  const handleStart = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const recorder = new MediaRecorder(stream)
    recorder.start()
    setStartDate(new Date())
    recorder.ondataavailable = (event) => {
      const mediaType = recorder.mimeType || event.data.type

      const extension = mediaType.split(';')[0].split('/')[1]
      const blob = new Blob([event.data])
      const file = new File([blob], `audio.${extension}`, { type: mediaType })
      upload.mutate(file)
    }
    setMediaStream(stream)
    setMediaRecorder(recorder)
  }

  const handleStop = () => {
    mediaRecorder?.stop()
    setStartDate(null)
    setMediaStream(null)
    setMediaRecorder(null)
  }

  const cogfy = useCogfy()
  const upload = useUpload(cogfy, {
    data: field.data,
    collectionId,
    fieldId: field.id,
    recordId: record.id,
    onProgress: setUploadProgress
  })

  const deleteFile = useMutation({
    mutationFn: async (id: UUID) => {
      await cogfy.deleteFile({ fileId: id })
    }
  })

  const isRecording = Boolean(mediaStream && mediaRecorder)
  const isFileUploading = upload.status === 'pending'
  const fileResult = (record.properties[field.id] as FileRecordProperty).file?.value

  return (
    <>
      {fileResult?.uploadStatus === 'sent' && fileResult.url && (
        <div className='flex items-center gap-2 group'>
          <audio
            controls
            src={fileResult.url}
          />
          {canEdit && (
            <Button
              onClick={() => setFileToDeleteId(fileResult?.id ?? null)}
              className='opacity-0 group-hover:opacity-100 transition-opacity duration-300'
              ghost
              square
              size="xs"
            >
              <TrashIcon size={16} />
            </Button>
          )}
        </div>
      )}
      {!fileResult && isRecording && (
        <div className="flex gap-2 items-center">
          <Button
            disabled={disabled}
            ghost
            size="sm"
            onClick={handleStop}
          >
            <MicIcon size={20} />
            Stop
          </Button>
          <AudioDuration
            startDate={startDate}
            handleStop={handleStop}
          />
        </div>
      )}
      {!fileResult && !isRecording && !isFileUploading && (
        <Button
          disabled={disabled}
          ghost
          size="sm"
          onClick={() => { handleStart() }}
        >
          <MicIcon size={20} />
          Start
        </Button>
      )}
      {isFileUploading &&
        <div className="flex">
          <span className="loading loading-spinner loading-xs"></span>
          {uploadProgress !== null && <span className="text-slate-500 ml-2">{uploadProgress} %</span>}
        </div>
      }

      {fileToDeleteId && (
        <ConfirmDeleteDialog
          title={`${t('Delete file')} '${fileResult?.name}'?`}
          open={fileToDeleteId !== null}
          onConfirm={() => {
            if (fileToDeleteId) {
              deleteFile.mutate(fileToDeleteId)
              setFileToDeleteId(null)
            }
          }}
          onClose={() => {
            setFileToDeleteId(null)
          }}
        />
      )}
    </>
  )
}
